import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import Img from '@components/Image/Image';
import { useSnackbar } from '@components/Provider';
import {
  CapaErrorCode,
  useFetchMyQuotationUserQuery,
  useUpdateTermsMutation
} from '@generated/graphql';
import useClient from '@hooks/useClient';
import useDashboard from '@hooks/useDashboard';
import useRequest from '@hooks/useRequest';
import { Box, Dialog, makeStyles } from '@material-ui/core';
import { TermsAndServiceAgreement } from '@routes/SignUp/Components';
import { TermsAndServiceState } from '@routes/SignUp/Components/TermsAndServiceAgreement/TermsAndServiceAgreement';
import {
  getStorageItem,
  STORAGE_ACTION,
  authStorageClear,
  clearStorageAll
} from '@utils/storage';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import Frame from './frame.svg';

const ServiceAgreeDialog = () => {
  const clientToken = getStorageItem(STORAGE_ACTION.CLIENT_ACCESS_TOKEN);
  const history = useHistory();

  const { data: fetchMeData } = useFetchMyQuotationUserQuery({
    fetchPolicy: 'network-only',
    skip: !clientToken
  });
  const { enqueueSnackbar } = useSnackbar();
  const [updateTerms] = useUpdateTermsMutation({
    refetchQueries: ['fetchMyQuotationUser'],
    awaitRefetchQueries: true
  });
  const { onInitClient } = useClient();
  const { onInitRfqState } = useRequest();
  const { onInitDashboard, onInitSelectedQuotation } = useDashboard();

  const { modalContainer } = useStyle();
  const [agreeState, setAgreeSate] = useState<TermsAndServiceState>({
    privacyAgree: false,
    promotionAgree: false,
    serviceAgree: false,
    paymentAgree: false
  });
  const open =
    fetchMeData?.fetchMyQuotationUser.result === CapaErrorCode.TermsNotAgreed;

  const hookFormDatas = useForm<TermsAndServiceState>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });
  const isValid =
    agreeState.privacyAgree &&
    agreeState.serviceAgree &&
    agreeState.paymentAgree;

  const onSubmit = async () => {
    try {
      const result = await updateTerms({
        variables: {
          input: {
            ...agreeState
          }
        }
      });
      if (result.data?.updateTerms.result !== CapaErrorCode.Success) {
        throw Error('error');
      }
    } catch (error) {
      enqueueSnackbar('동의에 실패하였습니다.', {
        variant: 'error'
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_ACTION.REDIRECT_PATH);
    authStorageClear();
    clearStorageAll();
    onInitClient();
    onInitRfqState();
    onInitDashboard();
    onInitSelectedQuotation();
    history.push('/login');
  };

  return (
    <Dialog className={modalContainer} open={open}>
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          padding="24px"
        >
          <Box display="flex" alignItems="center" gridGap={11}>
            <Img width={32} height={32} src={Frame} />
            <CustomizedTypography variant="body1" fontWeight="700">
              서비스 약관 동의
            </CustomizedTypography>
          </Box>
        </Box>
        <Box padding="0px 24px 24px 24px">
          <CustomizedTypography variant="caption" color="textSecondary">
            서비스 이용을 위해서는 이용 약관에 동의해야합니다.
          </CustomizedTypography>

          <FormProvider {...hookFormDatas}>
            <TermsAndServiceAgreement
              setAgreeState={setAgreeSate}
              agreeState={agreeState}
              checked={
                agreeState.privacyAgree &&
                agreeState.promotionAgree &&
                agreeState.serviceAgree &&
                agreeState.paymentAgree
              }
            />
          </FormProvider>
        </Box>
        <Box
          display="flex"
          justifyContent="center"
          gridGap={12}
          padding="6px 24px 24px 24px"
        >
          <CustomizedButton fullWidth variant="outlined" onClick={handleLogout}>
            나가기
          </CustomizedButton>
          <CustomizedButton fullWidth disabled={!isValid} onClick={onSubmit}>
            동의하기
          </CustomizedButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ServiceAgreeDialog;

const useStyle = makeStyles(
  {
    modalContainer: {
      '& .MuiDialog-paperWidthSm': {
        width: 343,
        borderRadius: 12
      }
    }
  },
  { classNamePrefix: 'ServiceAgreeDialog-' }
);
